import React from 'react';
import Toolbar from '@mui/material/Toolbar';
import styled from 'styled-components';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import { InitialAvatarName } from 'components/Avatar/InitialAvatar/styles';
import Avatar from 'components/Avatar/Avatar';

import { colors } from 'theme/colors';

import Notifications from './Notifications';

export const UserId = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: ${({ theme }) => theme.typography.body2.fontWeight};
  line-height: ${({ theme }) => theme.typography.body2.lineHeight};
  letter-spacing: 0.16px;
  margin: 0;
  margin-left: 8px;
`;

export const PopoverButton = styled.button`
  display: flex;
  align-items: center;
  margin: 8px 16px 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

export const SupportContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 6px 16px;
  background-color: ${({ theme }) => theme.palette.grey[100]};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const StyledPopoverText = styled.p`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.subtitle1.fontSize};
  letter-spacing: 0.15px;
  margin: 0px;
  margin-left: 8px;
`;

export const SideBarButton = styled.button`
  margin: 10px 0;
  display: flex;
  width: 34px;
  height: 34px;
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  background: ${({ theme }) => theme.palette.action.hover};
  border: none;
  cursor: pointer;
`;

export const StyledToolbar = styled(Toolbar)`
  background-color: ${({ theme }) => theme.palette.common.white};
  justify-content: space-between;
  min-height: 41px;
  padding: 5px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[300]};
`;

export const NavBarUser = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
`;

export const NavBarAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
  margin-left: 10px;

  ${InitialAvatarName} {
    font-size: ${({ theme }) => theme.typography.caption.fontSize};
  }
`;

export const StyledNotificationsIcon = styled(Notifications)`
  width: 24px;
  height: 24px;
  color: ${colors.ui.chip.grayText};
  margin-left: 8px;
  transition: 0.3s;

  :hover {
    color: ${({ theme }) => theme.palette.primary.dark};
  }
`;

export const UserModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 5px;
`;

export const TriggerLabel = styled.span`
  max-width: 160px;
  margin-left: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
  font-weight: ${({ theme }) => theme.typography.subtitle2.fontWeight};
  line-height: ${({ theme }) => theme.typography.subtitle2.lineHeight};

  @media (max-width: 900px) {
    display: none;
  }
`;

export const TriggerChevron = styled(KeyboardArrowDownRoundedIcon)`
  margin-left: 4px;
  font-size: ${({ theme }) => theme.typography.h3.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const MenuList = styled.div`
  min-width: 288px;
`;

export const MenuHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
`;

export const MenuAvatar = styled(Avatar)`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const UserName = styled.p`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.h5.fontSize};
  font-weight: ${({ theme }) => theme.typography.h5.fontWeight};
  line-height: ${({ theme }) => theme.typography.h5.lineHeight};
  margin: 0;
`;

export const UserEmail = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: ${({ theme }) => theme.typography.body2.fontWeight};
  line-height: ${({ theme }) => theme.typography.body2.lineHeight};
  margin: 0 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ProfileLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: ${({ theme }) => theme.typography.button.fontSize};
  font-weight: ${({ theme }) => theme.typography.button.fontWeight};
  line-height: ${({ theme }) => theme.typography.button.lineHeight};
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

export const MenuDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const AccountBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
`;

// A caption-sized section label; keeps the heavier weight, uppercase and tracking that
// mark it as an overline-style heading (theme overline is 11px, too small here).
export const SectionLabel = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  font-weight: ${({ theme }) => theme.typography.h5.fontWeight};
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin: 0 0 4px;
`;

export const AccountName = styled.p`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  font-weight: ${({ theme }) => theme.typography.body1.fontWeight};
  line-height: ${({ theme }) => theme.typography.body1.lineHeight};
  margin: 0;
`;

export const AccountNumber = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: ${({ theme }) => theme.typography.body2.fontWeight};
  line-height: ${({ theme }) => theme.typography.body2.lineHeight};
  margin: 0;
`;

export const MenuSection = styled.div`
  padding: 4px 0;
`;

export const MenuItemButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 12px 16px;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  font-weight: ${({ theme }) => theme.typography.body1.fontWeight};
  line-height: ${({ theme }) => theme.typography.body1.lineHeight};

  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
  }
`;

export const MenuFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`;

export const FooterButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: ${({ theme }) => theme.typography.button.fontSize};
  font-weight: ${({ theme }) => theme.typography.button.fontWeight};
  line-height: ${({ theme }) => theme.typography.button.lineHeight};

  &:hover {
    text-decoration: underline;
  }
`;

export const FooterLink = styled.a`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  font-weight: ${({ theme }) => theme.typography.body1.fontWeight};
  line-height: ${({ theme }) => theme.typography.body1.lineHeight};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
